/**
 * Finding a tool's mark without being told about the tool first.
 *
 * The backend discovers tools on its own schedule. One deploy added npm, pnpm,
 * yarn, typescript, php, composer, golang, cargo, gradle, android, cocoapods,
 * firebase, jupyter, postman, docker and flutter to a registry that is now 91
 * tools, and every one of them drew as a grey letter here because the icon set
 * was a list somebody maintained by hand. A list is always one deploy behind a
 * registry that grows.
 *
 * So the slug is derived from what the backend says, and the mark is fetched
 * as a static asset from this origin. A tool that appears tomorrow resolves
 * without a frontend change, and a page downloads only the marks it draws.
 *
 * Nothing here guesses what a tool *is*: it only tries to find a picture for a
 * name the backend already committed to. When no mark matches, the caller
 * falls back to the tool's own domain and then to a neutral letter, which is
 * the honest answer rather than a lookalike brand.
 */

import { BRAND_SLUGS } from './brandSlugs.generated';

export type BrandIcon = { hex: string; path: string };

/** Where scripts/gen-icon-modules.mjs writes the marks, same origin. */
const ASSET_BASE = '/brand-icons';

/**
 * Ecosystem names whose mark lives under the language's slug rather than the
 * tool's. Kept deliberately tiny: these are cases where no transformation of
 * the tool name reaches the brand, not a general-purpose icon map.
 */
const ECOSYSTEM: Record<string, string> = {
  cargo: 'rust',
  golang: 'go',
  gomod: 'go',
  pipenv: 'python',
  poetry: 'python',
  pip: 'python',
  rubygems: 'ruby',
  bundler: 'ruby',
  maven: 'apachemaven',
  sbt: 'scala',
  nuget: 'dotnet',
};

/** Strips a name down to the alphabet simple-icons slugs are written in. */
export function normalizeSlug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Slugs worth trying for a tool, best first.
 *
 * The backend's own iconSlug always outranks anything derived from an id,
 * because it is a statement rather than an inference.
 */
export function candidateSlugs(toolId: string, iconSlug?: string | null): string[] {
  const out: string[] = [];
  const push = (s: string | undefined | null) => {
    if (s && s.length > 1 && !out.includes(s)) out.push(s);
  };

  push(iconSlug ? normalizeSlug(iconSlug) : null);

  const id = normalizeSlug(toolId);
  push(id);
  push(ECOSYSTEM[id]);

  // vendor_product ids also answer to the product alone: microsoft_excel is
  // not in the set under either name, but adobe_photoshop is under photoshop.
  const parts = toolId.split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (parts.length > 1) {
    push(normalizeSlug(parts.slice(1).join('')));
    push(normalizeSlug(parts.join('')));
  }

  // golang -> go, nodejs -> node
  const trimmed = id.replace(/(lang|js)$/, '');
  if (trimmed.length > 1) push(trimmed);

  return out;
}

const cache = new Map<string, BrandIcon | null>();

/** True when a mark exists for this exact slug. Synchronous, no fetch. */
export function hasBrandModule(slug: string): boolean {
  return BRAND_SLUGS.has(slug);
}

/**
 * Loads the first mark that matches, or null when none does.
 *
 * Results are cached by tool so a list of a hundred rows does one lookup per
 * distinct tool rather than one per row.
 */
export async function loadBrandIcon(toolId: string, iconSlug?: string | null): Promise<BrandIcon | null> {
  const key = `${toolId} ${iconSlug ?? ''}`;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  for (const slug of candidateSlugs(toolId, iconSlug)) {
    // The index is consulted first so a tool with no mark never issues a
    // request, and the network tab stays free of 404s an operator would read
    // as a broken page.
    if (!BRAND_SLUGS.has(slug)) continue;
    try {
      const res = await fetch(`${ASSET_BASE}/${slug}.json`);
      if (!res.ok) continue;
      const [hex, path] = (await res.json()) as [string, string];
      if (typeof hex !== 'string' || typeof path !== 'string') continue;
      const icon = { hex, path };
      cache.set(key, icon);
      return icon;
    } catch {
      // A mark that fails to load is not worth failing a page over; fall
      // through to the next candidate and ultimately to the letter badge.
    }
  }
  cache.set(key, null);
  return null;
}

/**
 * Vendor names that prefix a product without identifying it. Dropping them is
 * what makes a letter badge useful: four tools that all begin "Microsoft"
 * otherwise draw the same grey M, so Word, Excel, PowerPoint and Office become
 * indistinguishable in exactly the list meant to tell them apart.
 */
const VENDOR_PREFIX = /^(microsoft|adobe|google|apple|amazon|aws|oracle|ibm|generic)\s+/i;

/**
 * The letters to draw when no mark exists.
 *
 * Taken from the product rather than the vendor, and never invented: this is
 * the tool's own name, abbreviated. It stays a letter badge on purpose, because
 * drawing an approximation of a withdrawn trademark would be a worse answer
 * than an honest initial.
 */
export function markInitials(label: string, toolId: string): string {
  const source = (label || toolId || '?').trim();
  const product = source.replace(VENDOR_PREFIX, '').trim() || source;
  const words = product.split(/[\s_-]+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 1).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/**
 * A stable tint for a tool with no mark.
 *
 * Deliberately muted and derived from the id, so the badge is consistent across
 * pages and two tools never collide by accident. It is an identity colour, not
 * a brand colour, and reads as such.
 */
export function markTint(toolId: string): string {
  let h = 0;
  for (let i = 0; i < toolId.length; i += 1) h = (h * 31 + toolId.charCodeAt(i)) % 360;
  return `hsl(${h} 42% 62%)`;
}
